import { useSelection } from '@spazious/dollhouse-engine';
import { FCompositionType } from '@spazious/dollhouse-graphql';
import { useGetFCompositionsSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { Translate, useTranslate } from '@spazious/dollhouse-i18n';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import { ToolModeEnum } from '@spazious/dollhouse-types';
import {
  Flex,
  Panel,
  PanelContent,
  PanelHeader,
  PanelProps,
  Typography,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { useDrawFsetup } from '../../hooks/index.js';
import { formatFCompositionSchemaName, parseFCompositionIcon } from '../../utils/index.js';

const Wrapper = styled(Flex)<{ active?: boolean }>`
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: var(--shape-rounded-radius);
  border: ${(props) =>
    props.active
      ? 'solid 1px var(--spz-sys-color-inverse-surface)'
      : 'solid 1px var(--spz-sys-color-surface-container-highest)'};
  background-color: ${(props) =>
    props.active
      ? 'var(--spz-sys-color-surface-container-lowest)'
      : 'var(--spz-sys-color-surface-variant)'};
  transition: border, background-color 0.2s ease-in-out;
  justify-content: center;
  align-items: center;
  cursor: grab;
  box-shadow: ${(props) =>
    props.active ? 'inset 0 0 0 1px var(--spz-sys-color-inverse-surface)' : 'none'};
  gap: var(--spacing-padding);
  &:hover {
    border: solid 1px var(--spz-sys-color-inverse-surface);
    box-shadow: inset 0 0 0 1px var(--spz-sys-color-inverse-surface);
    background-color: var(--spz-sys-color-surface-container-lowest);
  }
`;

const Card = styled(Flex)`
  border-radius: var(--shape-rounded-radius);
  padding: var(--spazing-padding);
  text-align: center;
  gap: var(--spacing-gap);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 2fr));
  grid-gap: calc(2 * var(--spacing-padding));
`;

export interface AssetCardProps {
  thumbnail: string;
  type: 'furniture' | 'decoration';
  label: string;
}

export const Image = styled.img`
  width: 60px;
  height: 60px;
`;

export const AssetCardMedia: React.FC<AssetCardProps> = ({ thumbnail, type, label }) => {
  return (
    <Wrapper>
      <Image alt={type} src={thumbnail} />
      {label}
    </Wrapper>
  );
};

export interface FArrangementsPanelProps extends PanelProps {}

export const FArrangementsPanel: React.FC<FArrangementsPanelProps> = () => {
  const spaceId = useGraph().graph.shallow.space;
  const ready = useStore((state) => state.status === 'ready');
  const set = useStore(({ set }) => set);
  const { select } = useSelection();
  const translate = useTranslate();
  const { node, set: setDrawFsetup } = useDrawFsetup();

  const { data } = useGetFCompositionsSuspenseQuery({
    skip: !ready || !spaceId,
    variables: {
      spaceId: `${spaceId}`,
      type: FCompositionType.Arrangement,
    },
  });

  const onClickFComposition = useEventCallback((nid: number) => {
    select([]);
    setDrawFsetup({
      node: { type: FShallowNodeTypesEnum.FComposition, id: nid },
    });
    set({
      showNavMesh: true,
      tool: ToolModeEnum.DrawFsetup,
    });
  });

  return (
    <Panel>
      <PanelHeader>
        <Flex column gap="var(--spacing-padding)">
          <Typography size="small" variant="headline">
            <Translate>Furniture Layouts</Translate>
          </Typography>
          <Flex alignItems="center">
            <Typography size="small" variant="title">
              <Translate>Select setup and draw a box in the scene.</Translate>
            </Typography>
          </Flex>
        </Flex>
      </PanelHeader>
      <PanelContent>
        <Grid>
          {(data?.fcompositions ?? []).map(({ id, nid, name }) => {
            const Icon = parseFCompositionIcon(nid);
            return (
              <Card key={id} column onClick={() => onClickFComposition(nid)}>
                <Wrapper column active={node?.id === nid}>
                  <Icon size={60} />
                  {/* i18n-extract-disable */}
                  {translate(formatFCompositionSchemaName(name))}
                </Wrapper>
              </Card>
            );
          })}
        </Grid>
      </PanelContent>
    </Panel>
  );
};
